import { useQuery } from '@tanstack/react-query';
import { Card, Empty, Spin, Steps } from 'antd';
import PropTypes from 'prop-types';
import { useState } from 'react';
import AgentArtifactPreview from './AgentArtifactPreview';
import { queryAgentChatResult } from '../services/chat';

const AGENT_RESULT_CARD_HEIGHT = 'calc(100vh - 128px)';

const CREATION_PHASES = [
  { entityTypes: ['seedPlan', 'hotPot'], key: 'source', label: '规划/热点' },
  { entityTypes: ['factAnchor'], key: 'fact', label: '材料搜集' },
  { entityTypes: ['topicLens'], key: 'topic', label: '选题策划' },
  { entityTypes: ['articleArc', 'scriptArc'], key: 'content', label: '图文/脚本' },
];

const createDisplayPhases = (artifacts = []) =>
  CREATION_PHASES.map((phase) => {
    const artifact = artifacts.find((item) => phase.entityTypes.includes(item?.entityType));
    return {
      ...phase,
      artifact,
      status: artifact ? 'finish' : 'wait',
    };
  });

const mergePendingEntryArtifact = (artifacts = [], pendingEntryArtifact) => {
  if (!pendingEntryArtifact) {
    return artifacts;
  }
  const hasSameArtifact = artifacts.some(
    (artifact) =>
      artifact?.entityId === pendingEntryArtifact.entityId &&
      artifact?.entityType === pendingEntryArtifact.entityType,
  );
  return hasSameArtifact ? artifacts : [pendingEntryArtifact, ...artifacts];
};

const AgentResultPanel = ({ organizationId, pendingEntryArtifact, threadId }) => {
  const [selectedPhaseKey, setSelectedPhaseKey] = useState();
  const { data: result, isFetching } = useQuery({
    enabled: Boolean(organizationId && threadId),
    queryFn: () => queryAgentChatResult({ organizationId, threadId }),
    queryKey: ['agents-chat', 'result', organizationId, threadId],
    refetchInterval: 3000,
  });

  const artifacts = mergePendingEntryArtifact(result?.artifacts || [], pendingEntryArtifact);
  const phases = createDisplayPhases(artifacts);
  const fallbackPhaseKey = phases.findLast((phase) => phase.artifact)?.key || phases[0]?.key;
  const currentPhaseKey = phases.some((phase) => phase.key === selectedPhaseKey)
    ? selectedPhaseKey
    : fallbackPhaseKey;
  const selectedPhaseIndex = Math.max(
    phases.findIndex((phase) => phase.key === currentPhaseKey),
    0,
  );
  const selectedPhase = phases[selectedPhaseIndex];

  return (
    <Card
      extra={
        <Steps
          current={selectedPhaseIndex}
          items={phases.map((phase) => ({
            key: phase.key,
            status: phase.status || 'wait',
            title: phase.label,
          }))}
          onChange={(index) => setSelectedPhaseKey(phases[index]?.key)}
          size="small"
          variant="outlined"
        />
      }
      styles={{
        body: {
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        },
        root: {
          display: 'flex',
          flexDirection: 'column',
          height: AGENT_RESULT_CARD_HEIGHT,
          maxHeight: AGENT_RESULT_CARD_HEIGHT,
        },
      }}
      title="创作预览"
      variant="borderless"
    >
      {!threadId && !pendingEntryArtifact ? (
        <Empty description="在左侧启动智能体后，这里查看阶段产出" />
      ) : (
        <Spin spinning={isFetching && !selectedPhase?.artifact}>
          {!selectedPhase?.artifact ? (
            <Empty
              description={
                selectedPhase?.label
                  ? `${selectedPhase.label}阶段暂无业务产出`
                  : '当前创作记录暂无业务产出'
              }
            />
          ) : (
            <AgentArtifactPreview artifact={selectedPhase.artifact} />
          )}
        </Spin>
      )}
    </Card>
  );
};

AgentResultPanel.propTypes = {
  organizationId: PropTypes.string,
  pendingEntryArtifact: PropTypes.shape({
    data: PropTypes.shape({}),
    entityId: PropTypes.string,
    entityType: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  threadId: PropTypes.string,
};

AgentResultPanel.defaultProps = {
  organizationId: undefined,
  pendingEntryArtifact: undefined,
  threadId: undefined,
};

export default AgentResultPanel;
